import clsx from 'clsx'
import { nothing, unsafeCSS } from 'lit'
import { customElement, property, query, state } from 'lit/decorators.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { html } from 'lit/static-html.js'

import { GenericFormElement } from '@/internal/mixin/genericFormElement'

import { CommonFormElementSize, CommonSize } from '../../common/types'
import styles from './textarea.scss?inline'

/**
 * `dap-ds-textarea`
 * @summary A textarea is a multi-line text input field.
 * @element dap-ds-textarea
 * @title - Textarea
 *
 * @property {'xs' | 'sm' | 'lg'} size - The size of the textarea. Default is `md`.
 * @property {boolean} disabled - The disabled state of the textarea.
 * @property {number} minlength - The minimum length of the textarea.
 * @property {string} value - The value of the textarea.
 * @property {string} tooltip - The tooltip of the textarea.
 * @property {'top' | 'right' | 'bottom' | 'left'} tooltipPlacement - The tooltip placement of the textarea.
 * @property {InputStatus} status - The status of the textarea. Can be `success` or `error`.
 * @property {boolean} readonly - The readonly state of the textarea.
 * @property {boolean} required - The required state of the textarea.
 * @property {string} label - The label of the textarea.
 * @property {string} description - The description of the textarea.
 * @property {string} feedback - The feedback of the textarea.
 * @property {'negative' | 'positive' | 'warning'} feedbackType - The feedback type of the textarea. Can be `negative`, `positive`, or `warning`.
 * @property {boolean} optional - The optional state of the textarea.
 * @property {string} optionalLabel - The optional label of the textarea.
 * @property {boolean} subtle - The weight of the label. Default is `false`
 * @property {boolean} autofocus - The autofocus state of the textarea.
 *
 * @event {{ value: number }} dds-count-change - Fires when the count of the textarea changes.
 * @event {{ value: string }} dds-change - Fired when the textarea value changes.
 * @event {{ value: string }} dds-input - Fired when the textarea value changes.
 * @event {{ value: string, originalEvent: Event }} dds-keydown - Fired when a key is pressed down.
 * @event {{ void }} dds-blur - Fired when the textarea loses focus.
 * @event {{ void }} dds-focus - Emitted when the textarea gains focus.
 * @event {{ value: string }} dds-select - Emitted when select text in textarea.
 *
 * @slot feedback-icon - The custom icon of the feedback.
 *
 * @csspart base - The main textarea container.
 * @csspart input - The native input of the textarea.
 * @csspart counter - The counter of the textarea.
 * @csspart counter-base - The base of the counter.
 * @csspart feedback - The feedback of the textarea.
 * @csspart feedback-base - The base of the feedback.
 * @csspart feedback-text - The text of the feedback.
 * @csspart feedback-icon - The icon of the feedback.
 *
 * @cssproperty --dds-textarea-min-rows - Minimum number of rows in the textarea. Default is 2.
 * @cssproperty --dds-textarea-spacing - Gap between elements in the textarea container. Default is var(--dds-spacing-200).
 * @cssproperty --dds-textarea-gap - Gap between elements in the textarea container. Default is var(--dds-spacing-200).
 * @cssproperty --dds-textarea-margin-top - Top margin of the textarea. Default is var(--dds-spacing-200).
 * @cssproperty --dds-textarea-padding - Padding of the textarea control. Default is var(--dds-spacing-200) var(--dds-spacing-300).
 * @cssproperty --dds-textarea-border-width - Border width of the textarea. Default is var(--dds-border-width-base).
 * @cssproperty --dds-textarea-border-color - Border color of the textarea. Default is var(--dds-border-neutral-base).
 * @cssproperty --dds-textarea-background - Background color of the textarea. Default is var(--dds-fields-background-default).
 * @cssproperty --dds-textarea-color - Text color of the textarea. Default is var(--dds-text-neutral-base).
 * @cssproperty --dds-textarea-border-radius - Border radius of the textarea. Default is var(--dds-radius-base).
 * @cssproperty --dds-textarea-disabled-bg - Background color when disabled. Default is var(--dds-fields-background-disabled).
 * @cssproperty --dds-textarea-disabled-color - Text color when disabled. Default is var(--dds-text-neutral-disabled).
 * @cssproperty --dds-textarea-readonly-bg - Background color when readonly. Default is var(--dds-fields-background-read-only).
 * @cssproperty --dds-textarea-readonly-color - Text color when readonly. Default is var(--dds-text-neutral-subtle).
 * @cssproperty --dds-textarea-error-border - Border color when in error state. Default is var(--dds-border-negative-base).
 * @cssproperty --dds-textarea-success-border - Border color when in success state. Default is var(--dds-border-positive-base).
 */
@customElement('dap-ds-textarea')
export default class DapDSTextarea extends GenericFormElement {
  /**@ignore */
  @query('#input') input!: HTMLTextAreaElement
  /** Hide character counter. */
  @property({ type: Boolean, reflect: true }) hideCounter = false
  /** The minimum length of the textarea. */
  @property({ type: Number }) minlength?: number
  /** The maximum length of the textarea. */
  @property({ type: Number }) maxlength?: number
  /** The number of rows in the textarea. */
  @property({ type: Number }) rows?: number = 6
  /** The number of columns in the textarea. */
  @property({ type: Number }) cols?: number
  /** The placeholder of the textarea. */
  @property() placeholder = ''
  /** Indicates how the control should wrap the value for form submission. */
  @property() wrap: 'hard' | 'soft'

  /**@ignore */
  @state() count = 0

  static readonly styles = unsafeCSS(styles)

  get focusElement() {
    return this.input
  }

  private _handleSlotChange(event: Event) {
    const target = event?.target as HTMLInputElement
    target.setAttribute('slot', 'icon')
  }

  async connectedCallback() {
    super.connectedCallback()

    await this.updateComplete
    this.input.value = this.value ?? ''
    this.count = this.input.value.length
  }

  attributeChangedCallback(name: string, oldValue: any, newValue: any) {
    super.attributeChangedCallback(name, oldValue, newValue)

    if (name === 'value' && this.input) {
      this.input.value = newValue
    }
  }

  onInput() {
    super.onInput()

    this.count = this.input.value.length
    this.value = this.input.value
    this.emit('dds-count-change', { value: this.count })

    this.setValidity()
  }

  onChange() {
    super.onChange()

    this.count = this.input.value.length
    this.value = this.input.value
    this.emit('dds-count-change', { value: this.count })

    this.setValidity()
  }

  onSelect(e: Event) {
    const target = e.target as HTMLTextAreaElement
    const selection = target.value.substring(
      target.selectionStart,
      target.selectionEnd,
    )
    this.emit('dds-select', { value: selection })
  }

  formResetCallback() {
    this.internals.setValidity({})
    this.internals.setFormValue(this.defaultValue || '')
    this.value = this.defaultValue || ''
    this.input.value = this.defaultValue || ''
  }

  handleInvalid(event: Event) {
    event.preventDefault()
  }

  private _renderFooter() {
    const counter = !this.hideCounter
      ? html`
    <dap-ds-typography
          part="counter"
          exportparts="base: counter-base"
          class=${clsx('textarea__counter', {
            'textarea__counter--error': this.status === 'error',
          })}
          variant="caption"
          size="${this.size as CommonFormElementSize}"
          id="counter"
          aria-hidden="true">
          ${this.count}
        </dap-ds-typography>
      </div>
    `
      : nothing

    return html`
      <div class="textarea__footer" part="footer">
        ${this.feedback && this._renderFeedback()}
        <span part="counter-container">${counter}</span>
      </div>
    `
  }

  render() {
    return this.renderLabel(
      undefined,
      html`
        <div
          part="base"
          class=${clsx(
            'textarea',
            `textarea--${this.size}`,
            { 'textarea--success': this.status === 'success' },
            { 'textarea--error': this.status === 'error' },
            { 'textarea--disabled': this.disabled },
            { 'textarea--readonly': this.readonly },
            { 'textarea--optional': this.optional },
          )}>
          <textarea
            id="input"
            part="input"
            class="textarea__control"
            aria-disabled=${this.disabled}
            aria-describedby="description ${this.feedback
              ? 'feedback'
              : nothing}"
            ?disabled=${this.disabled}
            ?readonly=${this.readonly}
            ?required=${this.required}
            minlength=${ifDefined(this.minlength)}
            maxlength=${ifDefined(this.maxlength)}
            wrap=${ifDefined(this.wrap)}
            cols=${ifDefined(this.cols)}
            rows=${ifDefined(this.rows)}
            placeholder=${ifDefined(this.placeholder)}
            ?autofocus=${this.autofocus}
            ?value=${this.value}
            aria-labelledby="label"
            @input=${this.onInput}
            @keydown=${this.onKeydown}
            @change=${this.onChange}
            @blur=${this.onBlur}
            @focus=${this.onFocus}
            @select=${this.onSelect}
            @invalid=${this.handleInvalid}></textarea>

          ${this._renderFooter()}
        </div>
      `,
    )
  }

  private _renderFeedback() {
    return html`
      <dap-ds-feedback
        part="feedback"
        feedbackType=${ifDefined(this.feedbackType)}
        feedbackSize=${ifDefined(this.size) as CommonSize}
        class=${clsx('textarea__feedback', {
          'textarea__feedback--sm': this.size === 'sm',
        })}
        id="feedback"
        exportparts="base:feedback-base, text:feedback-text, icon:feedback-icon"
        .subtle=${this.subtle}>
        <slot
          name="feedback-icon"
          @slotchange="${this._handleSlotChange}"></slot>
        ${this.feedback}
      </dap-ds-feedback>
    `
  }
}
